import { useFollowing } from "@serophin/nottldr-shared/hooks";
import { Loader2, UserCheck, UserPlus } from "lucide-preact";
import { useState } from "preact/hooks";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { useToast } from "../../contexts/ToastContext";
import { useIsSignedIn } from "../../platform/session";
import { DropdownItem } from "../article/Dropdown";

export type FollowButtonProps = {
  alias: string;
  display: "icon" | "dropdown-item" | "button";
};

const BUTTON_BASE = "flex items-center space-x-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors";
const FOLLOWING_CLASS = "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600";
const FOLLOW_CLASS = "bg-blue-600 hover:bg-blue-700 text-white";

type Follow = { following: boolean; isUpdating: boolean; toggle: (event: MouseEvent) => Promise<void> };

/** Follow state is the signed-in user's, so the prerender and a signed-out visitor see "Follow". */
const useFollowToggle = (alias: string): Follow => {
  const { isFollowing, follow, unfollow } = useFollowing();
  const signedIn = useIsSignedIn();
  const { showAuthModal } = useAuthModal();
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const following = signedIn && isFollowing(alias);

  const toggle = async (event: MouseEvent) => {
    event.preventDefault();
    if (isUpdating) {
      return;
    }
    if (!signedIn) {
      showAuthModal("Sign in to follow authors", "Create an account or sign in to follow authors and see their articles in your feed");
      return;
    }
    setIsUpdating(true);
    try {
      await (following ? unfollow(alias) : follow(alias));
    } catch {
      toast.error("Failed to update follow status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  return { following, isUpdating, toggle };
};

const FollowIcon = ({ following, isUpdating, color }: { following: boolean; isUpdating: boolean; color: string }) => {
  if (isUpdating) {
    return <Loader2 size={16} class={`animate-spin ${color}`} />;
  }
  return following ? <UserCheck size={16} class={color} fill="currentColor" /> : <UserPlus size={16} class={color} />;
};

const IconButton = ({ following, isUpdating, toggle }: Follow) => (
  <button
    type="button"
    onClick={toggle}
    disabled={isUpdating}
    class={`p-2 rounded-full transition-colors ${isUpdating ? "opacity-50 cursor-not-allowed" : ""} bg-black/50 hover:bg-black/75 backdrop-blur-sm`}
    aria-label={following ? "Unfollow" : "Follow"}
  >
    <FollowIcon following={following} isUpdating={isUpdating} color="text-white" />
  </button>
);

const TextButton = ({ following, isUpdating, toggle }: Follow) => (
  <button
    type="button"
    onClick={toggle}
    disabled={isUpdating}
    class={`${BUTTON_BASE} ${following ? FOLLOWING_CLASS : FOLLOW_CLASS} ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    <FollowIcon following={following} isUpdating={isUpdating} color={following ? "text-gray-600 dark:text-gray-400" : "text-white"} />
    <span>{following ? "Following" : "Follow"}</span>
  </button>
);

const MenuItem = ({ alias, following, isUpdating, toggle }: Follow & { alias: string }) => {
  const busyLabel = following ? "Unfollowing..." : "Following...";
  return (
    <DropdownItem
      icon={following ? UserCheck : UserPlus}
      isActive={following}
      label={isUpdating ? busyLabel : following ? "Unfollow" : "Follow"}
      onClick={toggle}
      data-test-click="follow-author"
      data-test-author={alias}
      data-test-action={following ? "unfollow" : "follow"}
    />
  );
};

export const FollowButton = ({ alias, display }: FollowButtonProps) => {
  const follow = useFollowToggle(alias);
  if (display === "icon") {
    return <IconButton {...follow} />;
  }
  return display === "button" ? <TextButton {...follow} /> : <MenuItem alias={alias} {...follow} />;
};
